import React from 'react';
import moment from 'moment';
import * as propz from 'propz';
import { styled, experimental_sx as sx } from '@mui/system';
import { SchoolEventTeamData, SchoolEventSchool, SchoolEvent } from 'models/event';
import { PublicEventKitsResponse } from 'models/kitLab';
import { ACTIVITIES_BLOCK_TYPE } from 'consts/common';
import {
  isInternalEvent,
  isHousesEvent,
  isIndividualSportEvent,
  isTeamSportEvent,
  isMultipartyEvent,
  isEventStatusCanceled,
  isTournamentEvent
} from 'helpers/event';
import { isIndividualTournament } from 'helpers/tournament';
import { getEventResultForCalendar } from 'helpers/calendar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import squadInTouchLogo from 'assets/logoImage.png';

interface Props {
  event: SchoolEvent;
  schoolId: string;
  type?: string;
  onEventClick: (event: SchoolEvent) => void;
  eventKitsData?: PublicEventKitsResponse | null;
}

const CardWrapper = styled(Box)(
  sx({
    position: 'relative',
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    p: 2,
    border: '1px solid rgba(0, 0, 0, 0.12)',
    overflow: 'hidden',
    borderRadius: '4px',
    background: '#fff',
    flex: {
      xs: '0 1 auto',
      md: '0 1 300px'
    },
    cursor: 'pointer',
    transition: '0.2s ease-in-out',

    '&:hover': {
      borderColor: '#1976d2'
    }
  })
);

const OpponentsWrapper = styled(Box)(
  sx({
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    height: '120px'
  })
);

const EventTimeWrapper = styled(Box)(
  sx({
    mt: 2
  })
);

const SportType = styled(Typography)(
  sx({
    height: '50px',
    textAlign: 'center',
    textTransform: 'uppercase',
    color: '#1976D2',
    mb: 1,
    flex: '0 1 auto'
  })
);

const Opponents = styled(Box)(
  sx({
    display: 'flex',
    justifyContent: 'space-around',
    gap: '10px'
  })
);

const SchoolLogoWrapper = styled(Box)(
  sx({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  })
);

const LOGO_SIZE = 80;

const SchoolLogo = styled(Box)(
  sx({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: `${LOGO_SIZE}px`,
    height: `${LOGO_SIZE}px`,
    textAlign: 'center',
    mb: 2,

    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      display: 'block'
    }
  })
);

const SchoolName = styled(Typography)(
  sx({
    color: '#ccc',
    fontSize: '12px',
    textAlign: 'center',
    lineHeight: '15px'
  })
);

const EventName = styled(Typography)(
  sx({
    height: '30px',
    textAlign: 'center',
    textTransform: 'uppercase',
    fontSize: '12px',
    fontWeight: 'bold',
    lineHeight: '15px',
    mb: 2
  })
);

const EventResult = styled(Typography)(
  sx({
    height: '15px',
    flex: 1,
    my: 2,
    textAlign: 'center',
    fontWeight: 'bold',
    color: '#1976D2'
  })
);

const EventTime = styled(Typography)(
  sx({
    textAlign: 'center',
    fontSize: '14px',
    color: '#919191'
  })
);

const CanceledOverlay = styled(Typography)(
  sx({
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontWeight: 'bold',
    color: 'red',
    fontSize: '150%',
    background: 'rgba(0,0,0,0.4)',

    '& span': {
      background: 'rgba(255,255,255,0.6)',
      p: 1,
      borderRadius: 2
    }
  })
);

export default function EventCard(props: Props) {
  const { event, schoolId, type, onEventClick } = props;

  const isTournamentEventCondition = isTournamentEvent(event);
  const tournament = propz.get(event, ['tournament']);
  const isIndividualTournamentEventCondition =
    isTournamentEventCondition && isIndividualTournament(tournament);

  const teamsData = (propz.get(event, ['teamsData'], []) || []) as SchoolEventTeamData[];

  const rawHousesData = propz.get(event, ['housesData'], []) || [];
  const housesData: any[] = Array.isArray(rawHousesData) ? rawHousesData.filter(Boolean) : [];

  const sportName = propz.get(event, ['sport', 'name'], '');
  const eventName = propz.get(event, ['generatedNames', schoolId], '');

  const isTypeResults = type === ACTIVITIES_BLOCK_TYPE.RESULTS;

  const rawInvitedSchools = propz.get(event, ['invitedSchools'], []) || [];
  const invitedSchools = Array.isArray(rawInvitedSchools)
    ? rawInvitedSchools.filter(Boolean)
    : [];

  const invitedSchoolsCount = invitedSchools.length;
  const isInvitedSchoolsOverOne = invitedSchoolsCount > 1;

  const firstInvitedSchool = invitedSchools[0] || {};
  const invitedSchoolPic = propz.get(firstInvitedSchool, ['pic'], '');
  const firstInvitedSchoolId = propz.get(firstInvitedSchool, ['id'], '');
  const firstInvitedSchoolName = propz.get(firstInvitedSchool, ['name'], '');

  const invitedSchoolsIds = invitedSchools.map((s: SchoolEventSchool) => s?.id);

  const isCurrentSchoolInvited = invitedSchoolsIds.includes(schoolId);
  const inviterSchoolLogo = propz.get(event, ['inviterSchool', 'pic'], '');
  const inviterSchoolId = propz.get(event, ['inviterSchoolId'], '');
  const inviterSchoolName = propz.get(event, ['inviterSchool', 'name'], '');

  const isInvitedSchoolLogoExist = invitedSchoolPic !== '';
  const invitedSchoolLogo =
    invitedSchoolsCount > 1 || !isInvitedSchoolLogoExist
      ? squadInTouchLogo
      : invitedSchoolPic;

  const startTime = propz.get(event, ['startTime'], '');
  const formattedDate = moment(startTime).format('D MMMM YYYY');
  const formattedTime = moment(startTime).format('h:mm a');

  const getHouseLogoSafe = (house: any, fallback: string) => {
    if (!house) {
      return fallback || squadInTouchLogo;
    }
    return (
      propz.get(house, ['pic'], '') ||
      propz.get(house, ['picture'], '') ||
      fallback
    );
  };

  const truncateLabel = (label: string, maxLength = 26): string => {
    if (!label) return '';
    return label.length > maxLength ? `${label.slice(0, maxLength - 1)}…` : label;
  };

  const getTeamNameForSchool = (schoolIdForTeam?: string | null): string => {
    if (!schoolIdForTeam) return '';
    const team = teamsData.find(t => t.schoolId === schoolIdForTeam);
    return propz.get(team || {}, ['name'], '');
  };

  const buildSideLabel = (sideSchoolId?: string | null, sideSchoolName?: string): string => {
    const safeSchoolName = sideSchoolName || '';
    if (!sideSchoolId) {
      return safeSchoolName;
    }

    const teamName = getTeamNameForSchool(sideSchoolId);

    if (sideSchoolId === schoolId) {
      return teamName || safeSchoolName;
    }

    if (teamName && safeSchoolName && teamName !== safeSchoolName) {
      return `${teamName} / ${safeSchoolName}`;
    }

    return teamName || safeSchoolName;
  };

  const renderSchoolLogos = (data: any) => {
    const {
      leftSideLogo,
      leftSideName,
      rightSideLogo,
      rightSideName,
      leftSideColors = [],
      rightSideColors = []
    } = data;

    const leftFullName = leftSideName || '';
    const rightFullName = rightSideName || '';

    const leftDisplayName = truncateLabel(leftFullName);
    const rightDisplayName = truncateLabel(rightFullName);

    return (
      <>
        <SchoolLogoWrapper>
          <SchoolLogo>
            <img src={leftSideLogo} alt={leftFullName} loading="lazy" />
          </SchoolLogo>
          <SchoolName title={leftFullName}>
            {leftDisplayName}
            {leftSideColors.length > 0 && (
              <span className="house-colors">
                {leftSideColors.map((color: string, index: number) => (
                  <span
                    key={index}
                    style={{
                      display: 'inline-block',
                      width: '10px',
                      height: '10px',
                      backgroundColor: color,
                      borderRadius: '50%',
                      marginLeft: '5px'
                    }}
                  ></span>
                ))}
              </span>
            )}
          </SchoolName>
        </SchoolLogoWrapper>

        <SchoolLogoWrapper>
          <SchoolLogo>
            <img src={rightSideLogo} alt={rightFullName} loading="lazy" />
          </SchoolLogo>
          <SchoolName title={rightFullName}>
            {rightDisplayName}
            {rightSideColors.length > 0 && (
              <span className="house-colors">
                {rightSideColors.map((color: string, index: number) => (
                  <span
                    key={index}
                    style={{
                      display: 'inline-block',
                      width: '10px',
                      height: '10px',
                      backgroundColor: color,
                      borderRadius: '50%',
                      marginLeft: '5px'
                    }}
                  ></span>
                ))}
              </span>
            )}
          </SchoolName>
        </SchoolLogoWrapper>
      </>
    );
  };

  const renderOpponents = () => {
    if (isHousesEvent(event) && Array.isArray(housesData) && housesData.length >= 2) {
      const leftHouse = housesData[0];
      const rightHouse = housesData[1];

      const leftHouseLogo = getHouseLogoSafe(leftHouse, inviterSchoolLogo || squadInTouchLogo);
      const rightHouseLogo = getHouseLogoSafe(
        rightHouse,
        invitedSchoolLogo || inviterSchoolLogo || squadInTouchLogo
      );

      const leftHouseColors = Array.isArray(leftHouse?.colors) ? leftHouse.colors : [];
      const rightHouseColors = Array.isArray(rightHouse?.colors) ? rightHouse.colors : [];

      return renderSchoolLogos({
        leftSideLogo: leftHouseLogo,
        leftSideName: propz.get(leftHouse, ['name'], ''),
        leftSideColors: leftHouseColors,
        rightSideLogo: rightHouseLogo,
        rightSideName: propz.get(rightHouse, ['name'], ''),
        rightSideColors: rightHouseColors
      });
    }

    if (isTournamentEvent(event) && invitedSchools.length === 2) {
      const firstInvited = invitedSchools[0] || {};
      const secondInvited = invitedSchools[1] || {};

      const firstInvitedId = propz.get(firstInvited, ['id'], '');
      const secondInvitedId = propz.get(secondInvited, ['id'], '');

      const firstInvitedLogo = propz.get(firstInvited, ['pic'], squadInTouchLogo);
      const firstInvitedBaseName = propz.get(firstInvited, ['name'], '');

      const secondInvitedLogo = propz.get(secondInvited, ['pic'], squadInTouchLogo);
      const secondInvitedBaseName = propz.get(secondInvited, ['name'], '');

      const firstInvitedName = buildSideLabel(firstInvitedId, firstInvitedBaseName);
      const secondInvitedName = buildSideLabel(secondInvitedId, secondInvitedBaseName);

      return renderSchoolLogos({
        leftSideLogo: firstInvitedLogo,
        leftSideName: firstInvitedName,
        rightSideLogo: secondInvitedLogo,
        rightSideName: secondInvitedName
      });
    }

    if (isTournamentEvent(event) && invitedSchools.length === 1) {
      const invitedSchoolId = firstInvitedSchoolId;
      const invitedSchoolTeams = teamsData.filter(
        (team: SchoolEventTeamData) => team.schoolId === invitedSchoolId
      );

      if (invitedSchoolTeams.length === 2) {
        const label = buildSideLabel(invitedSchoolId, firstInvitedSchoolName);

        return renderSchoolLogos({
          leftSideLogo: invitedSchoolLogo,
          leftSideName: label,
          rightSideLogo: invitedSchoolLogo,
          rightSideName: label
        });
      }
    }

    if (isIndividualTournamentEventCondition) {
      const label = buildSideLabel(inviterSchoolId, inviterSchoolName);

      return (
        <SchoolLogoWrapper>
          <SchoolLogo>
            <img
              src={inviterSchoolLogo}
              srcSet={inviterSchoolLogo}
              alt={inviterSchoolName}
              loading="lazy"
            />
          </SchoolLogo>
          <SchoolName title={label}>{truncateLabel(label)}</SchoolName>
        </SchoolLogoWrapper>
      );
    }

    if (
      (isInternalEvent(event) && isTeamSportEvent(event) && isMultipartyEvent(event)) ||
      (isInternalEvent(event) && isIndividualSportEvent(event))
    ) {
      const label = buildSideLabel(inviterSchoolId, inviterSchoolName);

      return (
        <SchoolLogoWrapper>
          <SchoolLogo>
            <img
              src={inviterSchoolLogo}
              srcSet={inviterSchoolLogo}
              alt={inviterSchoolName}
              loading="lazy"
            />
          </SchoolLogo>
          <SchoolName title={label}>{truncateLabel(label)}</SchoolName>
        </SchoolLogoWrapper>
      );
    }

    if (isInvitedSchoolsOverOne) {
      const leftLabel = buildSideLabel(inviterSchoolId, inviterSchoolName);

      return renderSchoolLogos({
        leftSideLogo: inviterSchoolLogo,
        leftSideName: leftLabel,
        rightSideLogo: invitedSchoolLogo,
        rightSideName: 'Various opponents'
      });
    }

    const invitedSchoolId = firstInvitedSchoolId;
    const invitedSchoolBaseName = firstInvitedSchoolName;

    if (isCurrentSchoolInvited) {
      const leftSchoolId = invitedSchoolId;
      const rightSchoolId = inviterSchoolId;

      const leftLabel = buildSideLabel(leftSchoolId, invitedSchoolBaseName);
      const rightLabel = buildSideLabel(rightSchoolId, inviterSchoolName);

      return renderSchoolLogos({
        leftSideLogo: invitedSchoolLogo,
        leftSideName: leftLabel,
        rightSideLogo: inviterSchoolLogo,
        rightSideName: rightLabel
      });
    }

    const leftSchoolId = inviterSchoolId;
    const rightSchoolId = invitedSchoolId;

    const leftLabel = buildSideLabel(leftSchoolId, inviterSchoolName);
    const rightLabel = buildSideLabel(rightSchoolId, invitedSchoolBaseName);

    return renderSchoolLogos({
      leftSideLogo: inviterSchoolLogo,
      leftSideName: leftLabel,
      rightSideLogo: invitedSchoolLogo,
      rightSideName: rightLabel
    });
  };

  const onCardClick = () => {
    const isOnEventClickExist = typeof onEventClick !== 'undefined';
    isOnEventClickExist && onEventClick(event);
  };

  return (
    <CardWrapper onClick={onCardClick}>
      {isEventStatusCanceled(event) && (
        <CanceledOverlay>
          <Box component="span">CANCELLED</Box>
        </CanceledOverlay>
      )}
      <SportType>{sportName}</SportType>

      <OpponentsWrapper>
        <Opponents>{renderOpponents()}</Opponents>
      </OpponentsWrapper>

      {isTypeResults && <EventResult>{getEventResultForCalendar(event, schoolId)}</EventResult>}

      <EventTimeWrapper sx={{ mt: 2 }}>
        <EventName>{eventName}</EventName>
        <EventTime>{formattedDate}</EventTime>
        <EventTime>{formattedTime}</EventTime>
      </EventTimeWrapper>
    </CardWrapper>
  );
}
